import { useRef } from "react";
import { motion, useInView } from "framer-motion";
import { ArrowRight, CheckCircle2 } from "lucide-react";

// Eyebrow component
function Eyebrow({ children }) {
  return <span className="eyebrow">{children}</span>;
}

// Glass card component
function GlassCard({ children, className = "" }) {
  return (
    <motion.div
      className={`glass-card p-6 md:p-8 ${className}`}
      whileHover={{ y: -4, borderColor: 'rgba(86, 205, 236, 0.3)' }}
      transition={{ duration: 0.3 }}
    >
      {children}
    </motion.div>
  );
}

// Stagger reveal
function StaggerReveal({ children, index = 0 }) {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-50px" });
  
  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y: 30 }}
      animate={isInView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.7, delay: index * 0.1, ease: [0.22, 1, 0.36, 1] }}
    >
      {children}
    </motion.div>
  );
}

export default function Portfolio() {
  const heroRef = useRef(null);
  const heroInView = useInView(heroRef, { once: true, amount: 0.3 });

  const sections = [
    {
      title: "Gaming Consoles",
      intro: "We maintain full inventory of current-generation and last-generation gaming consoles with priority stock allocation for high-volume buyers. Our console distribution covers:",
      items: [
        "PlayStation 5 Standard Edition, Digital Edition, and PS5 Pro",
        "PlayStation 4 Pro and PS4 Slim consoles",
        "Xbox Series X and Xbox Series S",
        "Nintendo Switch OLED, Nintendo Switch, and Switch Lite",
        "Steam Deck (all storage variants)",
        "Meta Quest 3 and Meta Quest Pro VR headsets"
      ],
      footer: "Annual import volume exceeds 1 million units with competitive wholesale pricing for retailers, distributors, and e-commerce operators."
    },
    {
      title: "Video Games",
      intro: "Thousands of titles across all major platforms including AAA releases, indie titles, limited editions, collector's editions, and pre-orders. We are authorized distributors for leading publishers:",
      items: [
        "Leading global publishers and first-party titles",
        "AAA blockbuster releases and pre-orders",
        "Indie games and digital-only titles",
        "Limited editions and collector's editions",
        "Legacy catalog and backward-compatible titles"
      ],
      footer: "Export volume reaches 500,000+ units annually to retailers across MENA, Europe, and Asia-Pacific regions."
    },
    {
      title: "Digital Gift Cards",
      intro: "We are one of the largest digital gift card distributors globally with 2 million+ cards sold annually. Our digital catalog includes:",
      items: [
        "PlayStation Network (PSN) cards — India, UAE, US, UK, EU regions",
        "Xbox Live and Xbox Game Pass subscriptions",
        "Steam Wallet codes — global denominations",
        "Nintendo eShop cards",
        "Google Play gift cards",
        "Apple App Store and iTunes cards",
        "Spotify, Netflix, and streaming service cards"
      ],
      footer: "Digital codes are available via API integration for marketplaces and D2C websites with real-time inventory and instant delivery capabilities."
    },
    {
      title: "Gaming Accessories",
      intro: "Premium gaming peripherals and accessories from official manufacturers and licensed third-party brands:",
      items: [
        "Controllers — DualSense, DualShock, Xbox Elite, Pro Controllers",
        "Gaming headsets — Sony, SteelSeries, Turtle Beach, HyperX",
        "Charging stations and docks",
        "VR accessories and peripherals",
        "Protective cases and storage solutions",
        "Gaming keyboards, mice, and PC peripherals"
      ],
      footer: null
    },
    {
      title: "B2B Distribution Services",
      intro: "Beyond product supply, we offer comprehensive distribution services designed for scale:",
      items: [
        "API Integration — Real-time inventory feed with 1,000+ SKUs for marketplaces and e-commerce platforms",
        "Dropshipping — B2B fulfillment with white-label packaging from our Dubai warehouse",
        "White Label Dashboard — Branded B2B portal with your identity, powered by our infrastructure",
        "Storage & Fulfillment — Climate-controlled warehousing in Dubai with pick-pack-ship services",
        "List with Us — Flat fee structure for suppliers to access our retail network"
      ],
      footer: null
    }
  ];

  return (
    <main data-testid="portfolio-page" className="bg-bg">
      {/* Hero */}
      <section ref={heroRef} className="pt-32 pb-16 md:pt-40 md:pb-20 relative">
        <div className="absolute inset-0 grid-bg opacity-20" />
        <motion.div
          className="max-w-4xl mx-auto px-fluid text-center relative z-10"
          initial={{ opacity: 0, y: 40 }}
          animate={heroInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8 }}
        >
          <Eyebrow>Distribution Portfolio</Eyebrow>
          <h1 className="font-heading text-hero font-extrabold uppercase tracking-wider mt-4">
            What We <span className="text-cyan">Distribute</span>
          </h1>
          <p className="mt-6 text-dim text-lg leading-relaxed max-w-2xl mx-auto">
            Optrenex International is a full-service B2B gaming distributor covering consoles, games, digital codes, and accessories across 41 countries.
          </p>
        </motion.div>
      </section>

      {/* Main Content */}
      <section className="pb-24 md:pb-32">
        <div className="max-w-4xl mx-auto px-fluid">
          {sections.map((section, idx) => (
            <StaggerReveal key={idx} index={idx}>
              <article className="mb-16">
                <h2 className="font-heading text-2xl md:text-3xl font-bold text-ink mb-4 border-l-4 border-cyan pl-4">
                  {section.title}
                </h2>
                <div className="text-dim space-y-4 leading-relaxed">
                  <p>{section.intro}</p>
                  <ul className="space-y-2 ml-4">
                    {section.items.map((item, i) => (
                      <li key={i} className="flex items-start gap-3">
                        <CheckCircle2 size={16} className="text-cyan mt-1 shrink-0" />
                        <span>{item}</span>
                      </li>
                    ))}
                  </ul>
                  {section.footer && (
                    <p>
                      <span className="text-ink font-medium">{section.footer}</span>
                    </p>
                  )}
                </div>
              </article>
            </StaggerReveal>
          ))}

          {/* CTA */}
          <StaggerReveal index={sections.length}>
            <div className="text-center pt-8 border-t border-line">
              <p className="text-dim mb-6">
                Ready to access our full product catalog and wholesale pricing?
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <motion.a
                  href="https://b2b.optrenex.com/register"
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="portfolio-register-btn"
                  className="btn-primary inline-flex items-center gap-2"
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                >
                  Register for B2B Access <ArrowRight size={16} />
                </motion.a>
                <motion.a
                  href="https://b2b.optrenex.com/login"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="btn-secondary inline-flex items-center gap-2"
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                >
                  Already a Partner? Login
                </motion.a>
              </div>
            </div>
          </StaggerReveal>
        </div>
      </section>
    </main>
  );
}
